<template>
  <div class="relative">
    <button
      @click="isOpen = !isOpen"
      class="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
    >
      <GlobeAltIcon class="h-5 w-5 text-gray-500" />
      <span class="text-sm font-medium text-gray-700">
        {{ appStore.currentDomain || i18n.t('console.selectDomain') }}
      </span>
      <ChevronDownIcon class="h-4 w-4 text-gray-500" />
    </button>

    <!-- Dropdown -->
    <div
      v-if="isOpen"
      class="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-10"
    >
      <!-- Search -->
      <div class="p-3 border-b border-gray-200">
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="i18n.t('console.searchDomain')"
          class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <!-- Domain List -->
      <div class="max-h-64 overflow-y-auto">
        <button
          v-for="zone in filteredZones"
          :key="zone.id"
          @click="selectDomain(zone)"
          class="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0"
        >
          <div class="font-medium text-sm text-gray-900">{{ zone.name }}</div>
          <div class="text-xs text-gray-500 mt-1">{{ zone.id }}</div>
        </button>

        <div v-if="filteredZones.length === 0" class="px-4 py-8 text-center text-sm text-gray-500">
          {{ i18n.t('console.noDomain') }}
        </div>
      </div>

      <!-- Loading -->
      <div v-if="appStore.zonesLoading" class="px-4 py-8 text-center">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <div class="mt-2 text-sm text-gray-600">{{ i18n.t('console.loading') }}</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useAppStore } from '../../stores/app';
import { useConfigStore } from '../../stores/config';
import { useI18nStore } from '../../stores/i18n';
import { GlobeAltIcon, ChevronDownIcon } from '@heroicons/vue/24/outline';
import DNSAPI from '../../api/dns';

const appStore = useAppStore();
const configStore = useConfigStore();
const i18n = useI18nStore();

const isOpen = ref(false);
const searchQuery = ref('');

const filteredZones = computed(() => {
  if (!searchQuery.value) {
    return appStore.zones;
  }
  const query = searchQuery.value.toLowerCase();
  return appStore.zones.filter(zone =>
    zone.name.toLowerCase().includes(query)
  );
});

const selectDomain = (zone) => {
  appStore.setCurrentDomain(zone.name, zone.id);
  isOpen.value = false;
};

const loadZones = async () => {
  if (!configStore.authConfig) return;

  appStore.setZonesLoading(true);
  try {
    const api = new DNSAPI(configStore.authConfig);
    const zones = await api.listZones({ per_page: 100 });
    appStore.setZones(zones);

    // 如果还没有选择域名，自动选择第一个
    if (!appStore.currentDomain && zones.length > 0) {
      appStore.setCurrentDomain(zones[0].name, zones[0].id);
    }
  } catch (error) {
    appStore.showError(`Failed to load domains: ${error.message}`);
  } finally {
    appStore.setZonesLoading(false);
  }
};

// 点击外部关闭下拉菜单
const handleClickOutside = (event) => {
  if (isOpen.value && !event.target.closest('.relative')) {
    isOpen.value = false;
  }
};

onMounted(() => {
  loadZones();
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

// 监听配置变化，重新加载域名
watch(() => configStore.isConfigured, (newVal) => {
  if (newVal) {
    loadZones();
  }
});
</script>
